import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Buildings,
  Users,
  UserCheck,
  CheckCircle,
  PauseCircle,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import type { Company } from "@/types";

function StatCard({
  icon: Icon,
  label,
  value,
  suffix,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  suffix?: string;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="size-4" />
        <span className="text-[11px] font-semibold uppercase tracking-widest">
          {label}
        </span>
      </div>
      <div className="mt-2">
        <span className="text-xl font-bold tabular-nums text-foreground">
          {value}
        </span>
        {suffix && (
          <span className="ml-1 text-xs text-muted-foreground">{suffix}</span>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: "active" | "suspended" }) {
  const isActive = status === "active";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        isActive
          ? "bg-emerald-500/10 text-emerald-600"
          : "bg-amber-500/10 text-amber-600"
      )}
    >
      {isActive ? (
        <CheckCircle className="size-3" />
      ) : (
        <PauseCircle className="size-3" />
      )}
      {isActive ? "Aktivna" : "Suspendovana"}
    </span>
  );
}

export interface CompanyDetailHeaderProps {
  company: Company;
}

export function CompanyDetailHeader({ company }: CompanyDetailHeaderProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Back + Header */}
      <div>
        <Link
          to="/dashboard/admin/companies"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Firme
        </Link>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/8">
            <Buildings className="size-6 text-primary" />
          </div>
          <div>
            <h1
              className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {company.name}
            </h1>
            <div className="mt-0.5 flex items-center gap-2 text-sm text-muted-foreground">
              <span>{company.planType}</span>
              <span>·</span>
              <span>{company.billingEmail}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={company.status} />
          {company.anonymizeEmployees && (
            <span className="inline-flex items-center rounded-full bg-purple-500/10 px-2 py-0.5 text-[11px] font-semibold text-purple-700">
              {t("admin.companies.anonymizeEmployees.badgeOn")}
            </span>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard
          icon={Users}
          label="Zaposleni"
          value={`${company.employeeCount} / ${company.maxEmployees}`}
        />
        <StatCard
          icon={UserCheck}
          label="Supervizori"
          value={String(company.supervisorCount)}
        />
      </div>
    </>
  );
}
